import { useEffect } from 'react';

const CHUNK_ERROR_RETRY_KEY = 'chunk_error_last_retry';
const CHUNK_ERROR_MAX_RETRIES = 2;
const CHUNK_ERROR_RETRY_WINDOW_MS = 30_000;

/**
 * Detects chunk loading failures (stale deployment) and auto-reloads.
 *
 * Uses sessionStorage to track retries within a 30s window so it won't
 * loop forever. After max retries, stops — the RouteErrorBoundary will
 * show a manual reload UI if the user navigates to a broken route.
 */
export function useChunkLoadErrorHandler() {
  useEffect(() => {
    const isChunkError = (str: string) =>
      str.includes('Failed to fetch dynamically imported module') ||
      str.includes('Importing a module script failed') ||
      str.includes('error loading dynamically imported module');

    const autoReload = () => {
      // Check retry count to prevent infinite loops
      const retryData = sessionStorage.getItem(CHUNK_ERROR_RETRY_KEY);
      let retryCount = 0;

      if (retryData) {
        try {
          const parsed = JSON.parse(retryData);
          if (Date.now() - parsed.timestamp < CHUNK_ERROR_RETRY_WINDOW_MS) {
            retryCount = parsed.count;
          }
        } catch {
          // Corrupted, reset
        }
      }

      if (retryCount >= CHUNK_ERROR_MAX_RETRIES) {
        console.warn('[ChunkLoadError] Max retries reached, not auto-reloading');
        sessionStorage.removeItem(CHUNK_ERROR_RETRY_KEY);
        return;
      }

      sessionStorage.setItem(
        CHUNK_ERROR_RETRY_KEY,
        JSON.stringify({ count: retryCount + 1, timestamp: Date.now() })
      );

      console.warn(`[ChunkLoadError] Auto-reloading (attempt ${retryCount + 1}/${CHUNK_ERROR_MAX_RETRIES})...`);

      // Unregister service workers AND clear caches to prevent serving stale chunks
      const cleanup = async () => {
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(r => r.unregister()));
          }
          // Delete all workbox/runtime caches so the reload fetches fresh chunks
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        } catch {
          // Best effort — proceed to reload regardless
        }
      };

      cleanup().then(() => {
        // Cache-busting hard navigation
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', Date.now().toString());
        window.location.replace(url.toString());
      });
    };

    const handleError = (event: ErrorEvent) => {
      const errorString = (event.error || event.message)?.toString() || '';
      if (isChunkError(errorString)) {
        event.preventDefault();
        autoReload();
      }
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reasonString = event.reason?.toString() || '';
      if (isChunkError(reasonString)) {
        event.preventDefault();
        autoReload();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
}
